export interface ProviderCurrencyCapability {
  currency: string;
  providerSupported?: boolean;
  managerEnabled?: boolean;
  effectiveActive?: boolean;
  effectiveStatus?: string;
  isActive: boolean;
  isPriority?: boolean;
}

export interface ProviderCurrencyRow {
  currency: string;
  isActive: boolean;
  managerEnabled: boolean;
  isPriority: boolean;
  providerSupported?: boolean;
  effectiveActive?: boolean;
  effectiveStatus?: string;
}

export interface ProviderCurrencyPatch {
  currency: string;
  isActive?: boolean;
  managerEnabled?: boolean;
  isPriority?: boolean;
}

/**
 * Reads the currency row returned by POST currency-manager.
 * Accepts either the capability itself or the `{ data: capability }` body
 * left by the API client, and ignores payloads that have no boolean isActive.
 */
export function readProviderCurrencyCapability(
  payload: unknown,
): ProviderCurrencyCapability | undefined {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }
  const record = payload as {
    currency?: unknown;
    isActive?: unknown;
    data?: unknown;
  };
  if (typeof record.currency === "string" && typeof record.isActive === "boolean") {
    return payload as ProviderCurrencyCapability;
  }
  if ("data" in record) {
    return readProviderCurrencyCapability(record.data);
  }
  return undefined;
}

export function applyProviderCurrencyCapability<
  T extends { currency: string; isPriority?: boolean },
>(
  rows: T[] | undefined,
  capability: ProviderCurrencyCapability | undefined,
): T[] | undefined {
  if (!rows || !capability?.currency || typeof capability.isActive !== "boolean") {
    return undefined;
  }
  const hasRow = rows.some((row) => row.currency === capability.currency);
  if (!hasRow) {
    return undefined;
  }
  return rows.map((row) => {
    if (row.currency === capability.currency) {
      return { ...row, ...capability };
    }
    if (capability.isPriority) {
      return { ...row, isPriority: false };
    }
    return row;
  });
}

export interface ProviderCurrencyEditor {
  readonly providerId: number | undefined;
  readonly rows: ProviderCurrencyRow[];
  readonly inFlight: boolean;
  sync(next: { providerId?: number; rows?: ProviderCurrencyRow[] }): void;
  beginStatusToggle(currency: string): ProviderCurrencyPatch | null;
  beginManagerToggle(currency: string): ProviderCurrencyPatch | null;
  beginPrioritySelect(currency: string): ProviderCurrencyPatch | null;
  beginPriorityClear(currency: string): ProviderCurrencyPatch | null;
  confirmIfCurrent(
    providerId: number,
    capability: ProviderCurrencyCapability | undefined,
  ): boolean;
  replaceRows(providerId: number, rows: ProviderCurrencyRow[]): void;
  fail(providerId: number): void;
}

function copyRows(rows: ProviderCurrencyRow[] | undefined): ProviderCurrencyRow[] {
  return rows ? rows.map((row) => ({ ...row })) : [];
}

/**
 * Status, manager, and priority clicks share one in-flight lock.
 * The lock flips synchronously, before React disables the control, so a second
 * click cannot repeat the boolean captured from the previous render.
 */
export function createProviderCurrencyEditor(initial?: {
  providerId?: number;
  rows?: ProviderCurrencyRow[];
}): ProviderCurrencyEditor {
  let providerId = initial?.providerId;
  let rows = copyRows(initial?.rows);
  let inFlight = false;

  const currentRow = (currency: string) =>
    rows.find((row) => row.currency === currency);

  return {
    get providerId() {
      return providerId;
    },
    get rows() {
      return rows;
    },
    get inFlight() {
      return inFlight;
    },
    sync(next) {
      if (next.providerId !== providerId) {
        providerId = next.providerId;
        rows = copyRows(next.rows);
        inFlight = false;
        return;
      }
      if (!inFlight && next.rows) {
        rows = copyRows(next.rows);
      }
    },
    beginStatusToggle(currency) {
      if (inFlight || providerId == null) {
        return null;
      }
      const row = currentRow(currency);
      if (!row) {
        return null;
      }
      inFlight = true;
      return { currency, isActive: !row.isActive };
    },
    beginManagerToggle(currency) {
      if (inFlight || providerId == null) {
        return null;
      }
      const row = currentRow(currency);
      if (!row) {
        return null;
      }
      inFlight = true;
      return { currency, managerEnabled: !row.managerEnabled };
    },
    beginPrioritySelect(currency) {
      if (inFlight || providerId == null) {
        return null;
      }
      const row = currentRow(currency);
      if (!row || row.isPriority) {
        return null;
      }
      inFlight = true;
      return { currency, isPriority: true };
    },
    beginPriorityClear(currency) {
      if (inFlight || providerId == null) {
        return null;
      }
      const row = currentRow(currency);
      if (!row || !row.isPriority) {
        return null;
      }
      inFlight = true;
      return { currency, isPriority: false };
    },
    confirmIfCurrent(expectedProviderId, capability) {
      if (providerId !== expectedProviderId) {
        return false;
      }
      const next = applyProviderCurrencyCapability(rows, capability);
      if (!next) {
        return false;
      }
      rows = next;
      inFlight = false;
      return true;
    },
    replaceRows(expectedProviderId, nextRows) {
      if (providerId !== expectedProviderId) {
        return;
      }
      rows = copyRows(nextRows);
      inFlight = false;
    },
    fail(expectedProviderId) {
      if (providerId !== expectedProviderId) {
        return;
      }
      inFlight = false;
    },
  };
}
